// @flow
import React from 'react';
import { PlatformSwitch, AppMobileHeader, SettingsMenu, AppMobileFooter, ConditionalFeature } from '@blocks';
import { Breadcrumb, Button, Icon } from '@elements';
import type { User, BundledPermissions } from '@types';
import { createNsTranslator, openSupportChat } from '@helpers/helpers';
import { withTranslation } from 'react-i18next';
import './BackupRequest.scss';

type Props = {
  isLoading: boolean,
  onRequest: Function,
  success: boolean,
  userEmail: string,
  user: User,
  onLogout: Function,
  t: Function,
  permissions: BundledPermissions,
};

class BackupRequest extends React.Component<Props> {
  // What a backup holds and, for whoever can request it, how it's delivered and its button. The others only read
  // what it holds: "sent to your email" isn't theirs, the notice says it goes to whoever asked.
  renderBackup(textKey: string, buttonKey: string, onClick: Function, canRequest: boolean) {
    const { isLoading } = this.props;
    const _ = createNsTranslator('settings', this.props.t);
    return (
      <React.Fragment>
        <p>
          <b>{_(`label/support/${textKey}/text/line1_title`)}&nbsp;</b>{_(`label/support/${textKey}/text/line1`)}
          {canRequest && _(`label/support/${textKey}/text/line2`)}
        </p>
        {canRequest && (
          <div className="requestButtonContainer">
            <Button
              className="secondary"
              onClick={onClick}
              loadingMessage={_('label/support/backup/wait')}
              isLoading={isLoading}
            >
              {_(`label/support/backup/${buttonKey}`)}
            </Button>
          </div>
        )}
      </React.Fragment>
    );
  }

  render() {
    const {
      success, userEmail, user, onLogout, permissions,
    } = this.props;
    // Only the clinic's administrators can request a backup (the API refuses the others): the rest read
    // why instead of buttons that could only fail
    const canRequest = Boolean(permissions.isManager);

    const _ = createNsTranslator('settings', this.props.t);

    const breadcrumbLinks = [
      { text: _('action/menu/support'), linkTo: '/ajustes/suporte' },
      { text: _('label/support/menu/backup') },
    ];


    let pageContent;
    if (success) {
      pageContent = (
        <React.Fragment>
          <h1>{_('label/support/backup/success/title')}</h1>
          {_('label/support/backup/success/text')} <br />
          <span className="userEmail">{userEmail}</span>
          <div className="requestButtonContainer">
            <Button
              className="primary"
              linkTo="/pacientes/lista"
            >
              Voltar ao Doutore
            </Button>
          </div>
        </React.Fragment>
      );
    } else {
      pageContent = (
        <React.Fragment>
          <div className="toolbar dashed">
            <h1>{_('label/support/menu/backup')}</h1>
          </div>
          <p>
            {_('label/support/backup/text/line1')}&nbsp;{_('label/support/backup/text/line2')}
            {canRequest && <React.Fragment>&nbsp;{_('label/support/backup/text/line3')}</React.Fragment>}
          </p>
          {canRequest ? <br/> : (
            <div className="backupManagerOnly" role="note">
              <Icon iconName="lock" size={18} className="backupManagerOnly-icon" />
              <div>
                <strong>{_('label/support/backup/manager_only/title')}</strong>
                <p>{_('label/support/backup/manager_only/text')}</p>
              </div>
            </div>
          )}

          {this.renderBackup('backup_excel', 'request_backup_patients', this.handleRequestPatientsBackup, canRequest)}
          {this.renderBackup('backup_complete', 'request_backup_complete', this.handleRequestCompleteBackup, canRequest)}

          <p>
            <br />{_('label/support/backup/support_action_lbl')}&nbsp;<a onClick={this.handleSupport}>{_('label/support/backup/support_action_btn')}</a>
          </p>
        </React.Fragment>
      );
    }

    return (
      <PlatformSwitch
        inject
        desktop={() => (
          <div className="appContent settingsPage" id="manualBackupPage">
            <aside className="sidebar">
              <SettingsMenu user={user} onLogout={onLogout} permissions={permissions} />
            </aside>
            <section className="mainSection">
              <ConditionalFeature
                enabled={permissions.permissionTo.backup}
                customMessage={_('label/backup_request_blocked')}
                hint={_('label/blocked/hint/global', 'global')}
                hideUpgradeButton
              >
                <div className="centerContent">
                  <Breadcrumb items={breadcrumbLinks} />
                  {pageContent}
                </div>
              </ConditionalFeature>
            </section>
          </div>
        )}
        mobile={() => (
          <React.Fragment>
            <AppMobileHeader title={_('label/support/menu/backup')} backLinkTo="/ajustes/suporte" />
            <div className="appContent settingsPage" id="manualBackupPage">
              <section className="mainSection">
                <ConditionalFeature
                  enabled={permissions.permissionTo.backup}
                  customMessage={_('label/backup_request_blocked')}
                  hint={_('label/blocked/hint/global', 'global')}
                  hideUpgradeButton
                >
                  <div className="centerContent">{pageContent}</div>
                </ConditionalFeature>
              </section>
            </div>
            <AppMobileFooter />
          </React.Fragment>
        )}
      />
    );
  }
  handleRequestPatientsBackup = () => {
    this.props.onRequest(false);
  };
  handleRequestCompleteBackup = () => {
    this.props.onRequest(true);
  };
  handleSupport = () => {
    openSupportChat();
  };
}

export default withTranslation()(BackupRequest);
